import { Glass } from "@coach/ui";
import { ShieldOff } from "lucide-react";
import { authClient } from "../lib/auth-client.js";

interface BlockedScreenProps {
  /** Reason from the server (`subscription_blocked`, `trial_exhausted`, `expired`, etc.). */
  reason?: string;
  /** Optional human-readable description from the API. */
  detail?: string;
  /** The current user's role in this org (`"owner"` | `"admin"` | `"member"` | …). */
  orgRole?: string;
  /** Email of the org owner — shown to non-owner members so they know who to contact. */
  ownerEmail?: string | null;
}

function headlineFor(reason?: string): string {
  if (reason === "trial_exhausted") return "Testzeitraum abgelaufen";
  if (reason === "expired" || reason === "license-expired") return "Lizenz abgelaufen";
  if (reason === "subscription_blocked" || reason === "subscription-blocked")
    return "Subscription nicht aktiv";
  return "Kein Zugriff";
}

const LANDING_URL: string =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (import.meta as any).env?.VITE_PUBLIC_LANDING_URL ?? "http://localhost:28781";

/**
 * Full-screen paywall shown when the license/subscription is no longer active.
 *
 * - **Owner / Admin**: "Lizenzen verwalten" → landing dashboard `/billing` + "Abmelden".
 * - **Member / Coach**: no manage button — shows owner email + "Abmelden".
 */
export function BlockedScreen({ reason, detail, orgRole, ownerEmail }: BlockedScreenProps) {
  // Treat unknown role as owner so solo accounts can always self-serve.
  const isOwner = !orgRole || orgRole === "owner" || orgRole === "admin";

  return (
    <div className="grid min-h-svh place-items-center bg-background p-6">
      <Glass level="floating" padding="lg" className="flex w-full max-w-md flex-col gap-6">
        {/* Icon badge */}
        <div className="flex justify-center">
          <span className="grid h-14 w-14 place-items-center rounded-full border border-tactical-red/30 bg-tactical-red/10 text-tactical-red">
            <ShieldOff className="h-7 w-7" />
          </span>
        </div>

        <header className="space-y-2 text-center">
          <p className="text-label-caps uppercase tracking-widest text-tactical-red">
            Zugriff gesperrt
          </p>
          <h1 className="text-display-sm font-semibold">{headlineFor(reason)}</h1>
          <p className="text-body-sm text-muted-foreground">
            {isOwner ? (
              (detail ??
              "Verwalte deine Lizenz im Dashboard, um wieder vollen Zugriff zu erhalten.")
            ) : ownerEmail ? (
              <>
                Bitte kontaktiere den Administrator deines Vereins, um die Lizenz zu überprüfen.
                <br />
                <a
                  href={`mailto:${ownerEmail}`}
                  className="mt-1 inline-block font-medium text-pitch-lime hover:underline"
                >
                  {ownerEmail}
                </a>
              </>
            ) : (
              "Bitte kontaktiere den Administrator deines Vereins, um die Lizenz zu überprüfen."
            )}
          </p>
        </header>

        <div className="flex flex-col gap-3">
          {isOwner && (
            <a
              href={`${LANDING_URL}/dashboard/billing`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-11 items-center justify-center rounded-md bg-pitch-lime text-body-md font-semibold text-[#1d3700] transition-shadow duration-fast ease-standard hover:shadow-glow"
            >
              Lizenzen verwalten
            </a>
          )}
          <button
            type="button"
            onClick={async () => {
              await authClient.signOut();
              window.location.href = "/";
            }}
            className="inline-flex h-11 items-center justify-center rounded-md border border-white/15 text-body-md font-medium text-muted-foreground hover:border-white/30 hover:text-foreground"
          >
            Abmelden
          </button>
        </div>
      </Glass>
    </div>
  );
}
